export interface Application {
  id: string;
  title: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  estimatedTime: string;
  description: string;
  problem: string;
  solution: string;
  steps: {
    step: number;
    title: string;
    description: string;
    image?: string;
    tips?: string;
  }[];
  tools: string[];
  materials: string[];
  safetyNotes: string[];
  relatedProducts: string[];
  videoUrl?: string;
  beforeImage?: string;
  afterImage?: string;
  tags: string[];
}

export const applications: Application[] = [
  // BELOW GRADE / UNDERGROUND APPLICATIONS
  {
    id: 'basement-waterproofing',
    title: 'Basement Waterproofing',
    category: 'Below Grade',
    difficulty: 'Intermediate',
    estimatedTime: '6-8 hours',
    description: 'Protect interior basement walls from moisture intrusion, prevent dampness, and repair cracks using Flexcrete.',
    problem: 'Moisture intrusion through basement walls, damp basements, foundation cracks, water stains, and window frame leaks.',
    solution: 'Apply Flexcrete to interior basement walls to create waterproof barrier. Repair cracks and seal window frames to prevent water intrusion.',
    steps: [
      {
        step: 1,
        title: 'Prepare Substrate',
        description: 'Remove all dust, loose materials, and grease stains. Clean per ASTM/DTU 21 guidelines.',
        tips: 'If substrate is porous, humidify to saturation before application'
      },
      {
        step: 2,
        title: 'Repair Cracks',
        description: 'Identify and repair any cracks in basement walls using Flexcrete.',
        image: '/images/basement-crack-repair.jpg'
      },
      {
        step: 3,
        title: 'Mix Flexcrete',
        description: 'Add 1 measure liquid to bucket, then 2 measures Type M Mortar. Mix 2-3 minutes until homogenous.',
        tips: 'Never add water to the mix. Consistency should be like thick milkshake.'
      },
      {
        step: 4,
        title: 'Apply First Coat',
        description: 'Apply Flexcrete to walls using roller, brush, or trowel. Ensure complete coverage.',
        image: '/images/basement-application.jpg'
      },
      {
        step: 5,
        title: 'Apply Second Coat',
        description: 'Wait 24 hours, then apply second coat perpendicular to first for maximum protection.',
        tips: 'First coat should not mark with fingernail before applying second coat'
      }
    ],
    tools: ['Paddle mixer', 'Paint roller', 'Trowel', 'Wire brush', 'Bucket'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Cleaning supplies'],
    safetyNotes: [
      'Work in well-ventilated areas',
      'Wear appropriate safety equipment',
      'Ensure no rain for 24 hours after application',
      'Apply only in temps 5-35°C (40-100°F)'
    ],
    relatedProducts: ['packaging-coverage', 'mixing-preparing', 'applying'],
    videoUrl: '/videos/basement-waterproofing.mp4',
    beforeImage: '/images/basement-before.jpg',
    afterImage: '/images/basement-after.jpg',
    tags: ['basement', 'waterproofing', 'below-grade', 'moisture-protection']
  },
  {
    id: 'foundation-protection',
    title: 'Foundation Protection',
    category: 'Below Grade',
    difficulty: 'Advanced',
    estimatedTime: '8-10 hours',
    description: 'Protect the critical join between foundation/slab and wall to prevent water intrusion.',
    problem: 'Water intrusion at foundation-wall joint, moisture damage, structural deterioration, and future water problems.',
    solution: 'Apply Flexcrete to foundation-wall cold joint for complete waterproofing. Works on both positive and negative water pressure.',
    steps: [
      {
        step: 1,
        title: 'Expose Foundation Joint',
        description: 'Clean and expose the foundation-to-wall joint area thoroughly.',
        tips: 'Remove all loose materials and debris from joint'
      },
      {
        step: 2,
        title: 'Prepare Surface',
        description: 'Clean substrate per ASTM guidelines. Remove dust, loose ends, and grease stains.',
        image: '/images/foundation-prep.jpg'
      },
      {
        step: 3,
        title: 'Apply Flashing',
        description: 'Apply Flexcrete generously at foundation-wall joint, extending 4-6 inches onto each surface.',
        tips: 'Build up thickness at this critical transition point'
      },
      {
        step: 4,
        title: 'Create Continuous Barrier',
        description: 'Ensure no gaps or thin spots. Flexcrete should form complete moisture barrier.',
        image: '/images/foundation-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Allow Cure and Test',
        description: 'Wait 24 hours for cure, then apply second coat if needed.',
        tips: 'Protect from rain and frost during first 24 hours'
      }
    ],
    tools: ['Trowel', 'Paddle mixer', 'Wire brush', 'Squeegee', 'Bucket'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Cleaning materials'],
    safetyNotes: [
      'Consult structural engineer for major foundation issues',
      'Wear appropriate safety equipment',
      'Work in proper temperature range',
      'Ensure adequate ventilation'
    ],
    relatedProducts: ['flashing', 'mixing-preparing', 'applying'],
    videoUrl: '/videos/foundation-protection.mp4',
    beforeImage: '/images/foundation-before.jpg',
    afterImage: '/images/foundation-after.jpg',
    tags: ['foundation', 'waterproofing', 'flashing', 'below-grade']
  },
  {
    id: 'cistern-tank-lining',
    title: 'Cisterns & Storage Tanks',
    category: 'Submerged',
    difficulty: 'Advanced',
    estimatedTime: '10-12 hours',
    description: 'Interior waterproof lining for concrete holding tanks, especially for RO water storage.',
    problem: 'Concrete tanks without waterproof lining, water seepage, corrosion from RO water, contamination from concrete leeching.',
    solution: 'Apply Flexcrete interior lining for completely waterproof tanks. Resistant to RO water corrosive effects.',
    steps: [
      {
        step: 1,
        title: 'Clean Tank Interior',
        description: 'Remove all dust, debris, and contaminants from tank interior surfaces.',
        tips: 'Tank must be completely dry before application'
      },
      {
        step: 2,
        title: 'Prepare Substrate',
        description: 'Ensure substrate is clean per ASTM guidelines. Repair any cracks or damage.',
        image: '/images/tank-prep.jpg'
      },
      {
        step: 3,
        title: 'Apply First Coat',
        description: 'Apply Flexcrete to all interior surfaces using roller or squeegee.',
        tips: 'Pay special attention to corners and seams'
      },
      {
        step: 4,
        title: 'Apply Additional Coats',
        description: 'Apply 2-3 coats total, waiting 24 hours between each coat.',
        image: '/images/tank-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Cure and Test',
        description: 'Allow full cure, then test with water before putting into service.',
        tips: 'Flexcrete is suitable for potable water storage'
      }
    ],
    tools: ['Paint roller', 'Squeegee', 'Paddle mixer', 'Bucket', 'Safety equipment'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Cleaning supplies'],
    safetyNotes: [
      'Ensure adequate ventilation in confined spaces',
      'Use proper confined space safety protocols',
      'Wear appropriate respiratory protection',
      'Have rescue plan for tank entry'
    ],
    relatedProducts: ['packaging-coverage', 'mixing-preparing', 'applying'],
    videoUrl: '/videos/tank-waterproofing.mp4',
    beforeImage: '/images/tank-before.jpg',
    afterImage: '/images/tank-after.jpg',
    tags: ['cistern', 'storage-tank', 'submerged', 'potable-water']
  },

  // ABOVE GRADE APPLICATIONS
  {
    id: 'retaining-wall-waterproofing',
    title: 'Retaining Wall Waterproofing',
    category: 'Above Grade',
    difficulty: 'Intermediate',
    estimatedTime: '8-12 hours',
    description: 'Prevent moisture and water intrusion from behind retaining walls to avoid efflorescence and protect wall coverings.',
    problem: 'Moisture intrusion from behind retaining walls, unsightly efflorescence deposits, damage to wall coverings, structural deterioration.',
    solution: 'Apply Flexcrete to prevent water intrusion from behind retaining walls. Eliminates efflorescence deposits and protects wall coverings.',
    steps: [
      {
        step: 1,
        title: 'Clean Wall Surface',
        description: 'Remove all dirt, dust, loose materials, and existing efflorescence deposits from wall surface.',
        tips: 'Power washing may be necessary for heavily soiled surfaces'
      },
      {
        step: 2,
        title: 'Repair Cracks and Damage',
        description: 'Repair any cracks or damaged areas in the retaining wall before waterproofing.',
        image: '/images/retaining-wall-repair.jpg'
      },
      {
        step: 3,
        title: 'Apply Flexcrete Membrane',
        description: 'Apply Flexcrete to the wall surface using roller, brush, or spray application.',
        tips: 'Ensure complete coverage with no thin spots or gaps'
      },
      {
        step: 4,
        title: 'Apply Second Coat',
        description: 'Wait 24 hours, then apply second coat for complete waterproofing protection.',
        image: '/images/retaining-wall-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Apply Finish Coating',
        description: 'After cure, apply desired wall covering or finish material.',
        tips: 'Flexcrete provides excellent substrate for various wall coverings'
      }
    ],
    tools: ['Roller', 'Brush', 'Paddle mixer', 'Power washer', 'Scaffolding'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Wall finish materials'],
    safetyNotes: [
      'Use proper scaffolding or lift equipment',
      'Wear fall protection on elevated work',
      'Check weather - no rain for 24 hours',
      'Ensure adequate ventilation'
    ],
    relatedProducts: ['applying', 'packaging-coverage', 'mixing-preparing'],
    videoUrl: '/videos/retaining-wall-waterproofing.mp4',
    beforeImage: '/images/retaining-wall-before.jpg',
    afterImage: '/images/retaining-wall-after.jpg',
    tags: ['retaining-wall', 'exterior', 'efflorescence', 'waterproofing']
  },
  {
    id: 'water-feature-waterproofing',
    title: 'Water Features & Waterfalls',
    category: 'Submerged',
    difficulty: 'Intermediate',
    estimatedTime: '6-8 hours',
    description: 'Waterproof interior of water features and waterfalls to keep water contained and prevent efflorescence.',
    problem: 'Water spillover from water features, leaking features, unsightly efflorescence deposits, structural damage from water escape.',
    solution: 'Waterproof inside of water feature basin with Flexcrete. Keeps water contained and prevents efflorescence deposits.',
    steps: [
      {
        step: 1,
        title: 'Clean Feature Interior',
        description: 'Drain and thoroughly clean all interior surfaces of water feature.',
        tips: 'Remove all algae, mineral deposits, and debris'
      },
      {
        step: 2,
        title: 'Repair Cracks and Damage',
        description: 'Repair any cracks or damaged areas in basin or spillover zones.',
        image: '/images/waterfall-repair.jpg'
      },
      {
        step: 3,
        title: 'Apply Flexcrete',
        description: 'Apply Flexcrete to all interior surfaces that will contact water.',
        tips: 'Pay special attention to spillover edges and high-flow areas'
      },
      {
        step: 4,
        title: 'Build Up Critical Areas',
        description: 'Apply extra coats to high-flow and spillover areas for added protection.',
        image: '/images/waterfall-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Cure and Test',
        description: 'Allow proper cure, then fill and test for leaks before final installation.',
        tips: 'Flexcrete prevents unsightly efflorescence stains'
      }
    ],
    tools: ['Trowel', 'Brush', 'Paddle mixer', 'Cleaning supplies', 'Bucket'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Decorative finish (optional)'],
    safetyNotes: [
      'Wear appropriate safety equipment',
      'Ensure proper ventilation',
      'Use slip-resistant footwear around water',
      'Disconnect all electrical before working'
    ],
    relatedProducts: ['applying', 'mixing-preparing', 'packaging-coverage'],
    videoUrl: '/videos/waterfall-waterproofing.mp4',
    beforeImage: '/images/waterfall-before.jpg',
    afterImage: '/images/waterfall-after.jpg',
    tags: ['waterfall', 'water-feature', 'decorative', 'efflorescence']
  },
  {
    id: 'planter-waterproofing',
    title: 'Planter Waterproofing',
    category: 'Above Grade',
    difficulty: 'Beginner',
    estimatedTime: '4-6 hours',
    description: 'Waterproof interior of planters to keep water inside and prevent stains and efflorescence deposits.',
    problem: 'Water leaking from planters, unsightly efflorescence deposits, staining of surrounding areas, structural damage from water escape.',
    solution: 'Waterproof inside of planter with Flexcrete. Keeps water contained and prevents efflorescence deposits.',
    steps: [
      {
        step: 1,
        title: 'Clean Planter Interior',
        description: 'Remove all soil, debris, and contaminants from planter interior.',
        tips: 'Planter must be completely dry before application'
      },
      {
        step: 2,
        title: 'Repair Cracks',
        description: 'Repair any cracks or damaged areas in planter before waterproofing.',
        image: '/images/planter-repair.jpg'
      },
      {
        step: 3,
        title: 'Apply Flexcrete',
        description: 'Apply Flexcrete to all interior surfaces of the planter.',
        tips: 'Ensure complete coverage including corners and seams'
      },
      {
        step: 4,
        title: 'Apply Second Coat',
        description: 'Wait 24 hours, then apply second coat for complete waterproofing.',
        image: '/images/planter-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Cure and Test',
        description: 'Allow proper cure, then test with water before adding soil and plants.',
        tips: 'Flexcrete prevents water damage and efflorescence'
      }
    ],
    tools: ['Brush', 'Trowel', 'Paddle mixer', 'Cleaning supplies', 'Bucket'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Cleaning supplies'],
    safetyNotes: [
      'Wear appropriate safety equipment',
      'Ensure adequate ventilation',
      'Work in proper temperature range',
      'Allow complete cure before use'
    ],
    relatedProducts: ['applying', 'mixing-preparing', 'packaging-coverage'],
    videoUrl: '/videos/planter-waterproofing.mp4',
    beforeImage: '/images/planter-before.jpg',
    afterImage: '/images/planter-after.jpg',
    tags: ['planter', 'decorative', 'efflorescence', 'waterproofing']
  },
  {
    id: 'roof-waterproofing',
    title: 'Roof Waterproofing - Flat & Pitched',
    category: 'Above Grade',
    difficulty: 'Advanced',
    estimatedTime: '12-16 hours',
    description: 'Repair and waterproof both flat and pitched roofs. Repair rusted panels, chimney flashing, cracked tiles.',
    problem: 'Water intrusion through roof, chimney flashing failures, cracked roof tiles, rusted galvanized panels, leaks between roof levels.',
    solution: 'Apply Flexcrete to repair entire roof surface. UV resistant formula protects against sun damage. Perfect for flashing applications.',
    steps: [
      {
        step: 1,
        title: 'Safety Setup',
        description: 'Set up proper safety equipment including harness, ladder stabilizers, and non-slip shoes.',
        tips: 'Never work on wet roof. Wait for completely dry conditions.'
      },
      {
        step: 2,
        title: 'Prepare Roof Surface',
        description: 'Clean all areas to be waterproofed. Remove rust, loose materials, and debris.',
        image: '/images/roof-prep.jpg'
      },
      {
        step: 3,
        title: 'Repair Chimney Flashing',
        description: 'Apply Flexcrete to chimney and other utility vent flashing areas.',
        tips: 'Extend flashing 4-6 inches onto both surfaces'
      },
      {
        step: 4,
        title: 'Waterproof Roof Surface',
        description: 'Apply Flexcrete to entire roof surface or damaged areas as needed.',
        image: '/images/roof-application.jpg'
      },
      {
        step: 5,
        title: 'Flash Level Changes',
        description: 'Apply flashing at transitions between different roof levels.',
        tips: 'These are critical waterproofing points'
      }
    ],
    tools: ['Ladder', 'Safety harness', 'Paddle mixer', 'Roller', 'Squeegee'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Safety equipment'],
    safetyNotes: [
      'Always use proper fall protection',
      'Work with partner when possible',
      'Check weather - no rain for 24 hours',
      'Secure all tools to prevent falls'
    ],
    relatedProducts: ['flashing', 'applying', 'packaging-coverage'],
    videoUrl: '/videos/roof-waterproofing.mp4',
    beforeImage: '/images/roof-before.jpg',
    afterImage: '/images/roof-after.jpg',
    tags: ['roof', 'waterproofing', 'flashing', 'above-grade']
  },
  {
    id: 'deck-terrace-patio-waterproofing',
    title: 'Decks, Terraces & Patios Waterproofing',
    category: 'Above Grade',
    difficulty: 'Intermediate',
    estimatedTime: '8-12 hours',
    description: 'Prevent moisture and water penetration into slabs. Repair cracks and provide better substrate for tile or other deck coverings.',
    problem: 'Water penetration into slab, cracks in concrete, poor substrate for tile, moisture damage to underlying structure.',
    solution: 'Apply Flexcrete to entire deck surface to prevent water penetration. Provides excellent substrate for tile, cement, or other deck coverings.',
    steps: [
      {
        step: 1,
        title: 'Prepare Deck Surface',
        description: 'Clean entire deck surface. Repair any cracks and remove loose materials.',
        tips: 'Remove all dust, grease, and contaminants'
      },
      {
        step: 2,
        title: 'Repair Cracks',
        description: 'Repair all cracks in the deck surface using Flexcrete.',
        image: '/images/deck-crack-repair.jpg',
        tips: 'Ensure all cracks are properly filled and sealed'
      },
      {
        step: 3,
        title: 'Apply Flexcrete',
        description: 'Apply Flexcrete to entire deck surface using roller, brush, or trowel.',
        tips: 'Ensure complete coverage with no thin spots or gaps'
      },
      {
        step: 4,
        title: 'Create Proper Drainage',
        description: 'If needed, use Flexcrete to create slope for proper drainage.',
        image: '/images/deck-drainage.jpg',
        tips: 'Proper drainage prevents water pooling'
      },
      {
        step: 5,
        title: 'Apply Second Coat',
        description: 'Wait 24 hours, apply second coat for complete waterproofing.',
        tips: 'Ready for tile, cement, or other deck coverings'
      }
    ],
    tools: ['Trowel', 'Roller', 'Paddle mixer', 'Squeegee', 'Level'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Crack repair materials'],
    safetyNotes: [
      'Use proper fall protection on elevated decks',
      'Ensure adequate ventilation',
      'Check weather conditions',
      'Wear appropriate safety equipment'
    ],
    relatedProducts: ['applying', 'mixing-preparing', 'packaging-coverage'],
    videoUrl: '/videos/deck-waterproofing.mp4',
    beforeImage: '/images/deck-before.jpg',
    afterImage: '/images/deck-after.jpg',
    tags: ['deck', 'terrace', 'patio', 'slab', 'waterproofing']
  },
  {
    id: 'balcony-deck-waterproofing',
    title: 'Balcony, Deck & Terrace Waterproofing',
    category: 'Above Grade',
    difficulty: 'Intermediate',
    estimatedTime: '8-12 hours',
    description: 'Critical flashing at wall-balcony joints. Seal entire deck to prevent moisture intrusion.',
    problem: 'Water penetration at wall-balcony joints, moisture migration loosening tile, standing water, cracks in concrete.',
    solution: 'Use Flexcrete for wall-balcony cold joint flashing (highly recommended). Seal entire balcony including underside.',
    steps: [
      {
        step: 1,
        title: 'Prepare Deck Surface',
        description: 'Clean entire deck surface. Repair any cracks and remove loose materials.',
        tips: 'Remove all dust, grease, and contaminants'
      },
      {
        step: 2,
        title: 'Flash Wall-Deck Joint',
        description: 'Apply Flexcrete generously at wall-balcony cold joint. This is critical!',
        image: '/images/balcony-flashing.jpg',
        tips: 'Extend 4-6 inches onto both wall and deck surfaces'
      },
      {
        step: 3,
        title: 'Create Pitch for Drainage',
        description: 'If needed, use Flexcrete to create slope diverting water away from wall.',
        tips: 'Proper drainage prevents water pooling'
      },
      {
        step: 4,
        title: 'Waterproof Entire Deck',
        description: 'Apply Flexcrete to entire deck surface including bottom side.',
        image: '/images/deck-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Apply Second Coat',
        description: 'Wait 24 hours, apply second coat for complete waterproofing.',
        tips: 'Ready for tile or other finish materials'
      }
    ],
    tools: ['Trowel', 'Roller', 'Paddle mixer', 'Squeegee', 'Level'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Crack repair materials'],
    safetyNotes: [
      'Use proper fall protection on elevated decks',
      'Ensure adequate ventilation',
      'Check weather conditions',
      'Wear appropriate safety equipment'
    ],
    relatedProducts: ['flashing', 'applying', 'mixing-preparing'],
    videoUrl: '/videos/balcony-waterproofing.mp4',
    beforeImage: '/images/balcony-before.jpg',
    afterImage: '/images/balcony-after.jpg',
    tags: ['balcony', 'deck', 'terrace', 'flashing', 'waterproofing']
  },
  {
    id: 'facade-waterproofing',
    title: 'Façade Waterproofing',
    category: 'Above Grade',
    difficulty: 'Intermediate',
    estimatedTime: '10-14 hours',
    description: 'Spray protective waterproof membrane on exterior façade to prevent moisture intrusion.',
    problem: 'Moisture intrusion through stucco, efflorescence deposits, poor stucco adhesion, weather deterioration.',
    solution: 'Spray or apply Flexcrete protective membrane on façade. Increases stucco adherence and prevents moisture damage.',
    steps: [
      {
        step: 1,
        title: 'Clean Façade Surface',
        description: 'Remove all dirt, dust, loose materials, and efflorescence deposits.',
        tips: 'Power washing may be necessary for heavily soiled surfaces'
      },
      {
        step: 2,
        title: 'Repair Damaged Areas',
        description: 'Repair any cracks or damaged areas in façade before waterproofing.',
        image: '/images/facade-repair.jpg'
      },
      {
        step: 3,
        title: 'Apply Flexcrete Membrane',
        description: 'Spray or roll Flexcrete onto façade surface in even coats.',
        tips: 'Can use hopper gun for spray application - contact us for guidance'
      },
      {
        step: 4,
        title: 'Ensure Complete Coverage',
        description: 'Verify complete coverage with no thin spots or gaps.',
        image: '/images/facade-application.jpg'
      },
      {
        step: 5,
        title: 'Apply Finish Coating',
        description: 'After cure, apply stucco or other cementitious façade covering.',
        tips: 'Flexcrete provides superior substrate for façade materials'
      }
    ],
    tools: ['Hopper gun (optional)', 'Roller', 'Paddle mixer', 'Power washer', 'Scaffolding'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Façade finish materials'],
    safetyNotes: [
      'Use proper scaffolding or lift equipment',
      'Wear fall protection on elevated work',
      'Use respiratory protection for spray application',
      'Check weather - no rain for 24 hours'
    ],
    relatedProducts: ['applying', 'packaging-coverage', 'mixing-preparing'],
    videoUrl: '/videos/facade-waterproofing.mp4',
    beforeImage: '/images/facade-before.jpg',
    afterImage: '/images/facade-after.jpg',
    tags: ['facade', 'exterior', 'stucco', 'waterproofing']
  },
  {
    id: 'waterproof-flashing',
    title: 'Waterproof Flashing',
    category: 'General',
    difficulty: 'Advanced',
    estimatedTime: '10-16 hours',
    description: 'Critical waterproofing at joints, seams, and transitions where different materials or planes meet. Primary defense against water intrusion.',
    problem: 'Water intrusion at joints where different materials meet, moisture damage at changes of plane, wood rot, mildew growth, structural deterioration from water infiltration.',
    solution: 'Apply Flexcrete flashing at all critical joints and transitions. Prevents water infiltration where different materials or planes meet. Essential for structural protection.',
    steps: [
      {
        step: 1,
        title: 'Identify Critical Areas',
        description: 'Locate all changes in plane (wall-floor, wall-deck, roof levels) and joints where different materials meet (concrete-block, tile-concrete, etc.).',
        tips: 'These are primary entry points for water intrusion - flashing is your primary defense'
      },
      {
        step: 2,
        title: 'Clean and Prepare Joints',
        description: 'Remove all debris, dust, and loose materials from joints. Ensure surfaces are clean per ASTM guidelines.',
        image: '/images/flashing-prep.jpg',
        tips: 'Proper preparation is critical for flashing adhesion'
      },
      {
        step: 3,
        title: 'Apply Flashing at Changes of Plane',
        description: 'Apply Flexcrete generously at wall-floor, wall-deck, and roof level transitions. This is critical for structural protection.',
        image: '/images/flashing-application.jpg',
        tips: 'Extend flashing 4-6 inches onto each surface for best protection'
      },
      {
        step: 4,
        title: 'Flash Material Joints',
        description: 'Apply Flexcrete at all joints where different materials meet - concrete-block, tile-concrete, chimney-roof, etc.',
        image: '/images/material-joint-flashing.jpg',
        tips: 'Any time two different materials meet, you have a seam that is never waterproof'
      },
      {
        step: 5,
        title: 'Create Continuous Barrier',
        description: 'Ensure no gaps or thin spots. Flexcrete should form complete moisture barrier at all critical points.',
        tips: 'Flashing acts as a shield to guide water away from structural elements'
      },
      {
        step: 6,
        title: 'Allow Proper Cure',
        description: 'Let flashing cure for 24 hours before applying additional coats or finish materials.',
        tips: 'Protect from rain and frost during first 24 hours'
      }
    ],
    tools: ['Trowel', 'Paddle mixer', 'Wire brush', 'Squeegee', 'Bucket', 'Level'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Cleaning materials'],
    safetyNotes: [
      'Work in proper temperature range (5-35°C)',
      'Wear appropriate safety equipment',
      'Ensure adequate ventilation',
      'Check weather - no rain for 24 hours',
      'Use fall protection for elevated work'
    ],
    relatedProducts: ['mixing-preparing', 'applying', 'packaging-coverage'],
    videoUrl: '/videos/flashing-guide.mp4',
    beforeImage: '/images/flashing-before.jpg',
    afterImage: '/images/flashing-after.jpg',
    tags: ['flashing', 'joints', 'transitions', 'waterproofing', 'structural-protection']
  },

  // SUBMERGED APPLICATIONS
  {
    id: 'pool-waterproofing',
    title: 'Pool Waterproofing',
    category: 'Submerged',
    difficulty: 'Advanced',
    estimatedTime: '16-24 hours',
    description: 'Complete waterproofing for swimming pools. Designed for submerged applications.',
    problem: 'Pool leaks, efflorescence behind tile, cracks in structure, leaks around fittings, water loss.',
    solution: 'Waterproof entire inside of pool with Flexcrete. Highly recommended behind tile line. Repair leaks and cracks. Designed for submerged use.',
    steps: [
      {
        step: 1,
        title: 'Drain and Clean Pool',
        description: 'Completely drain pool/spa and clean all surfaces thoroughly.',
        tips: 'Remove all algae, dirt, and deposits'
      },
      {
        step: 2,
        title: 'Repair Cracks and Leaks',
        description: 'Repair all cracks and leaks around fittings using Flexcrete.',
        image: '/images/pool-repair.jpg'
      },
      {
        step: 3,
        title: 'Apply Behind Tile Line',
        description: 'Apply Flexcrete behind tile line to prevent efflorescence (highly recommended).',
        tips: 'This prevents unsightly white deposits'
      },
      {
        step: 4,
        title: 'Waterproof Entire Interior',
        description: 'Apply 2-3 coats of Flexcrete to entire pool/spa interior.',
        image: '/images/pool-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Cure and Fill',
        description: 'Allow complete cure per manufacturer specs, then fill and test.',
        tips: 'Flexcrete can be used as final finish or under tile'
      }
    ],
    tools: ['Trowel', 'Roller', 'Paddle mixer', 'Pool cleaning equipment', 'Squeegee'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Tile adhesive (if tiling)'],
    safetyNotes: [
      'Ensure proper ventilation in pool area',
      'Use slip-resistant footwear',
      'Follow confined space protocols if applicable',
      'Never work alone in empty pool'
    ],
    relatedProducts: ['packaging-coverage', 'applying', 'mixing-preparing'],
    videoUrl: '/videos/pool-waterproofing.mp4',
    beforeImage: '/images/pool-before.jpg',
    afterImage: '/images/pool-after.jpg',
    tags: ['pool', 'submerged', 'waterproofing']
  },
  {
    id: 'spa-waterproofing',
    title: 'Spa Waterproofing',
    category: 'Submerged',
    difficulty: 'Advanced',
    estimatedTime: '12-18 hours',
    description: 'Complete waterproofing for spas. Designed for submerged applications.',
    problem: 'Spa leaks, efflorescence behind tile, cracks in structure, leaks around fittings, water loss.',
    solution: 'Waterproof entire inside of spa with Flexcrete. Highly recommended behind tile line. Repair leaks and cracks. Designed for submerged use.',
    steps: [
      {
        step: 1,
        title: 'Drain and Clean Spa',
        description: 'Completely drain spa and clean all surfaces thoroughly.',
        tips: 'Remove all algae, dirt, and deposits'
      },
      {
        step: 2,
        title: 'Repair Cracks and Leaks',
        description: 'Repair all cracks and leaks around fittings using Flexcrete.',
        image: '/images/spa-repair.jpg'
      },
      {
        step: 3,
        title: 'Apply Behind Tile Line',
        description: 'Apply Flexcrete behind tile line to prevent efflorescence (highly recommended).',
        tips: 'This prevents unsightly white deposits'
      },
      {
        step: 4,
        title: 'Waterproof Entire Interior',
        description: 'Apply 2-3 coats of Flexcrete to entire spa interior.',
        image: '/images/spa-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Cure and Fill',
        description: 'Allow complete cure per manufacturer specs, then fill and test.',
        tips: 'Flexcrete can be used as final finish or under tile'
      }
    ],
    tools: ['Trowel', 'Roller', 'Paddle mixer', 'Spa cleaning equipment', 'Squeegee'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Tile adhesive (if tiling)'],
    safetyNotes: [
      'Ensure proper ventilation in spa area',
      'Use slip-resistant footwear',
      'Follow confined space protocols if applicable',
      'Never work alone in empty spa'
    ],
    relatedProducts: ['packaging-coverage', 'applying', 'mixing-preparing'],
    videoUrl: '/videos/spa-waterproofing.mp4',
    beforeImage: '/images/spa-before.jpg',
    afterImage: '/images/spa-after.jpg',
    tags: ['spa', 'submerged', 'waterproofing']
  },
  {
    id: 'fountain-waterfeature',
    title: 'Fountains & Water Features',
    category: 'Submerged',
    difficulty: 'Intermediate',
    estimatedTime: '6-8 hours',
    description: 'Waterproof inside of basins and spillover areas to keep water contained.',
    problem: 'Water spillover from fountains, leaking features, efflorescence deposits, structural damage from water escape.',
    solution: 'Waterproof inside of basin with Flexcrete. Keeps water contained and prevents efflorescence deposits.',
    steps: [
      {
        step: 1,
        title: 'Clean Feature Interior',
        description: 'Drain and thoroughly clean all interior surfaces of water feature.',
        tips: 'Remove all algae, mineral deposits, and debris'
      },
      {
        step: 2,
        title: 'Repair Cracks',
        description: 'Repair any cracks or damaged areas in basin or spillover zones.',
        image: '/images/fountain-repair.jpg'
      },
      {
        step: 3,
        title: 'Apply Flexcrete',
        description: 'Apply Flexcrete to all interior surfaces that will contact water.',
        tips: 'Pay special attention to spillover edges'
      },
      {
        step: 4,
        title: 'Build Up Critical Areas',
        description: 'Apply extra coats to high-flow and spillover areas for added protection.',
        image: '/images/fountain-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Cure and Test',
        description: 'Allow proper cure, then fill and test for leaks before final installation.',
        tips: 'Flexcrete prevents unsightly efflorescence stains'
      }
    ],
    tools: ['Trowel', 'Brush', 'Paddle mixer', 'Cleaning supplies', 'Bucket'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Decorative finish (optional)'],
    safetyNotes: [
      'Wear appropriate safety equipment',
      'Ensure proper ventilation',
      'Use slip-resistant footwear around water',
      'Disconnect all electrical before working'
    ],
    relatedProducts: ['applying', 'mixing-preparing', 'packaging-coverage'],
    videoUrl: '/videos/fountain-waterproofing.mp4',
    beforeImage: '/images/fountain-before.jpg',
    afterImage: '/images/fountain-after.jpg',
    tags: ['fountain', 'water-feature', 'submerged', 'decorative']
  },
  {
    id: 'bathroom-shower-waterproofing',
    title: 'Bathroom & Shower Pan Waterproofing',
    category: 'Interior',
    difficulty: 'Intermediate',
    estimatedTime: '6-10 hours',
    description: 'Keep water inside shower pans and insulate moisture from escaping bathroom enclosure.',
    problem: 'Water escaping shower, moisture damage to walls/floors, leaks through bathroom floor, mold growth.',
    solution: 'Use Flexcrete to waterproof entire shower pan. Creates impermeable barrier and perfect substrate for tile.',
    steps: [
      {
        step: 1,
        title: 'Prepare Shower Pan',
        description: 'Clean and prepare shower pan surface. Ensure proper slope to drain.',
        tips: 'Surface must be clean and free of debris'
      },
      {
        step: 2,
        title: 'Apply First Coat',
        description: 'Apply Flexcrete to entire shower pan floor and walls up to splash zone.',
        image: '/images/shower-application.jpg'
      },
      {
        step: 3,
        title: 'Waterproof Corners and Seams',
        description: 'Build up thickness at corners and wall-floor transitions.',
        tips: 'These are critical waterproofing points'
      },
      {
        step: 4,
        title: 'Apply Second Coat',
        description: 'Wait 24 hours, then apply second coat for complete waterproofing.',
        image: '/images/shower-waterproofing.jpg'
      },
      {
        step: 5,
        title: 'Install Tile',
        description: 'After cure, install tile using appropriate adhesive.',
        tips: 'Flexcrete provides excellent substrate for tile'
      }
    ],
    tools: ['Trowel', 'Brush', 'Paddle mixer', 'Level', 'Measuring tools'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Tile and adhesive'],
    safetyNotes: [
      'Ensure adequate bathroom ventilation',
      'Wear appropriate safety equipment',
      'Test waterproofing before tiling',
      'Follow building codes for wet areas'
    ],
    relatedProducts: ['applying', 'flashing', 'mixing-preparing'],
    videoUrl: '/videos/bathroom-waterproofing.mp4',
    beforeImage: '/images/bathroom-before.jpg',
    afterImage: '/images/bathroom-after.jpg',
    tags: ['bathroom', 'shower', 'interior', 'waterproofing', 'tile-substrate']
  },
  {
    id: 'crack-repair',
    title: 'Crack Repair',
    category: 'General',
    difficulty: 'Beginner',
    estimatedTime: '2-4 hours',
    description: 'Repair cracks in concrete, masonry, stucco, and other surfaces to prevent water infiltration and structural damage.',
    problem: 'Cracks in walls, floors, foundations, or concrete surfaces allowing water intrusion, leading to further deterioration, efflorescence, and structural damage.',
    solution: 'Use Flexcrete to fill and seal cracks, creating a waterproof barrier that prevents water infiltration and stops crack propagation.',
    steps: [
      {
        step: 1,
        title: 'Identify and Clean Crack',
        description: 'Locate all cracks and clean them thoroughly. Remove loose materials, dust, and debris from the crack.',
        tips: 'Use a wire brush or compressed air to clean out the crack completely'
      },
      {
        step: 2,
        title: 'Widen if Necessary',
        description: 'For hairline cracks, widen slightly with a chisel or grinder to create a better surface for Flexcrete adhesion.',
        image: '/images/crack-preparation.jpg',
        tips: 'Create a V-groove for better material penetration'
      },
      {
        step: 3,
        title: 'Prepare Flexcrete',
        description: 'Mix Flexcrete according to standard instructions - 1 part liquid to 2 parts Type M Mortar.',
        tips: 'For crack filling, mix to a thicker consistency than normal application'
      },
      {
        step: 4,
        title: 'Fill the Crack',
        description: 'Apply Flexcrete into the crack using a trowel or putty knife. Ensure complete filling with no air pockets.',
        image: '/images/crack-filling.jpg',
        tips: 'Overfill slightly and smooth flush with surrounding surface'
      },
      {
        step: 5,
        title: 'Apply Surface Coat',
        description: 'After the crack fill has cured (24 hours), apply a thin coat of Flexcrete over the entire repair area.',
        tips: 'Extend the coating 4-6 inches beyond the crack on all sides for complete waterproofing'
      },
      {
        step: 6,
        title: 'Finish and Protect',
        description: 'Allow to cure completely. Can be painted, tiled over, or left as final finish.',
        tips: 'Protect from rain and frost for first 24 hours'
      }
    ],
    tools: ['Trowel or putty knife', 'Wire brush', 'Chisel or grinder (optional)', 'Paddle mixer', 'Bucket'],
    materials: ['Flexcrete liquid', 'Type M Mortar', 'Cleaning supplies'],
    safetyNotes: [
      'Wear safety glasses when chiseling or grinding',
      'Use dust mask when cleaning cracks',
      'Wear appropriate safety equipment',
      'Work in proper temperature range (5-35°C)',
      'Ensure no rain for 24 hours after application'
    ],
    relatedProducts: ['mixing-preparing', 'applying', 'packaging-coverage'],
    videoUrl: '/videos/crack-repair.mp4',
    beforeImage: '/images/crack-before.jpg',
    afterImage: '/images/crack-after.jpg',
    tags: ['crack-repair', 'maintenance', 'general', 'waterproofing', 'structural-repair']
  }
];

export const applicationCategories = [
  'All',
  'Below Grade',
  'Above Grade',
  'Submerged',
  'Interior',
  'General',
  'Roofing',
  'Foundation',
  'Decorative',
  'Water Features',
  'Retaining Walls',
  'Planters'
];

export const difficultyLevels = ['All', 'Beginner', 'Intermediate', 'Advanced'];
